import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Bot, FolderKanban, GitBranch, History, Home, ListTodo, PenTool, Plus, Search } from 'lucide-react'
import type { Repo, Taak } from '../../gedeeld/types'
import { api, github } from '../lib/api'
import { sleutelVan, statusLabel } from '../lib/opmaak'
import { navigeer } from '../lib/router'
import { lees, nieuweTaak, openTaak, useStaat, zet } from '../lib/staat'
import { Punt } from './ui'
import { SoortIcoon } from './taak/stukjes'

/* ===========================================================================
 *  Zoeken, en meteen ergens heen
 *
 *  Eén veld voor alles: een taaknummer (WEB-12), een woord uit een titel,
 *  een projectnaam, een repo, of wat je wilt doen ("nieuw whiteboard"). Met
 *  de pijltjes kies je, met Enter ga je. Wie met Projects werkt, zit hier
 *  vaker dan in het menu.
 * =========================================================================== */

interface Regel {
  key: string
  groep: string
  icoon: ReactNode
  titel: ReactNode
  sub?: ReactNode
  doe: () => void
}

let reposOnthouden: Repo[] | null = null

export default function Zoeken() {
  const projecten = useStaat((s) => s.projecten)
  const githubAan = useStaat((s) => s.github.ingesteld)
  const [q, setQ] = useState('')
  const [taken, setTaken] = useState<Taak[]>([])
  const [repos, setRepos] = useState<Repo[]>(reposOnthouden ?? [])
  const [keuze, setKeuze] = useState(0)
  const lijst = useRef<HTMLDivElement>(null)

  const sluit = () => zet({ zoekOpen: false })

  useEffect(() => {
    if (!githubAan || reposOnthouden) return
    github.repos().then((r) => { reposOnthouden = r; setRepos(r) }).catch(() => { /* zoeken werkt ook zonder */ })
  }, [githubAan])

  /* Taken op de server zoeken, even na het typen. */
  useEffect(() => {
    const t = q.trim()
    if (t.length < 2) { setTaken([]); return }
    const klok = setTimeout(() => {
      api.zoeken(t).then((x) => setTaken(x.taken)).catch(() => setTaken([]))
    }, 220)
    return () => clearTimeout(klok)
  }, [q])

  const regels = useMemo<Regel[]>(() => {
    const t = q.trim().toLowerCase()
    const past = (s: string) => !t || s.toLowerCase().includes(t)
    const uit: Regel[] = []

    const acties: Regel[] = [
      { key: 'a-taak', groep: 'Doen', icoon: <Plus size={15} />, titel: 'Nieuwe taak', doe: () => nieuweTaak({}) },
      { key: 'a-project', groep: 'Doen', icoon: <FolderKanban size={15} />, titel: 'Nieuw project', doe: () => navigeer('/projecten?nieuw=1') },
      { key: 'a-wb', groep: 'Doen', icoon: <PenTool size={15} />, titel: 'Nieuw whiteboard', doe: () => navigeer('/whiteboards?nieuw=1') },
      { key: 'a-repo', groep: 'Doen', icoon: <GitBranch size={15} />, titel: 'Nieuwe repository', doe: () => navigeer('/code?nieuw=1') },
      { key: 'g-over', groep: 'Ga naar', icoon: <Home size={15} />, titel: 'Overzicht', doe: () => navigeer('/') },
      { key: 'g-mijn', groep: 'Ga naar', icoon: <ListTodo size={15} />, titel: 'Mijn werk', doe: () => navigeer('/mijn') },
      { key: 'g-log', groep: 'Ga naar', icoon: <History size={15} />, titel: 'Changelog', doe: () => navigeer('/changelog') },
      { key: 'g-ai', groep: 'Ga naar', icoon: <Bot size={15} />, titel: 'Assistent', doe: () => navigeer('/ai') },
    ]
    uit.push(...acties.filter((a) => past(String(a.titel))).slice(0, t ? 4 : 8))

    for (const tk of taken.slice(0, 12)) {
      const sleutel = sleutelVan(tk, projecten)
      uit.push({
        key: 't-' + tk.id, groep: 'Taken', icoon: <SoortIcoon soort={tk.soort} />,
        titel: <>{sleutel && <span className="mono stil">{sleutel}</span>} {tk.titel}</>,
        sub: `${statusLabel(tk.status)}${tk.toegewezenNaam ? ' · ' + tk.toegewezenNaam : ''}`,
        doe: () => openTaak(tk.id),
      })
    }

    for (const p of projecten.filter((p) => past(`${p.naam} ${p.sleutel ?? ''}`)).slice(0, t ? 6 : 5)) {
      uit.push({
        key: 'p-' + p.id, groep: 'Projecten', icoon: <Punt kleur={p.kleur} />,
        titel: p.naam, sub: p.sleutel ?? undefined,
        doe: () => navigeer(`/p/${p.id}/bord`),
      })
    }

    if (t) {
      for (const r of repos.filter((r) => past(`${r.naam} ${r.omschrijving ?? ''}`)).slice(0, 6)) {
        uit.push({
          key: 'r-' + r.naam, groep: 'Repositories', icoon: <GitBranch size={15} />,
          titel: r.naam, sub: r.omschrijving ?? undefined,
          doe: () => navigeer(`/code/${r.naam}`),
        })
      }
    }
    return uit
  }, [q, taken, projecten, repos])

  useEffect(() => { setKeuze(0) }, [q])

  useEffect(() => {
    lijst.current?.querySelector('.zoek-regel.gekozen')?.scrollIntoView({ block: 'nearest' })
  }, [keuze])

  function doe(r: Regel | undefined) {
    if (!r) return
    sluit()
    r.doe()
  }

  function toets(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setKeuze((k) => Math.min(regels.length - 1, k + 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setKeuze((k) => Math.max(0, k - 1)) }
    if (e.key === 'Enter') { e.preventDefault(); doe(regels[keuze]) }
    if (e.key === 'Escape') { e.preventDefault(); sluit() }
  }

  let vorige = ''
  return createPortal(
    <div className="scherm zoek-scherm" onMouseDown={(e) => { if (e.target === e.currentTarget) sluit() }}>
      <div className="zoek" role="dialog" aria-label="Zoeken">
        <div className="zoek-veld">
          <Search size={17} />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={toets}
            placeholder="Zoek op taaknummer, titel, project of repo…" />
          <kbd>Esc</kbd>
        </div>
        <div className="zoek-lijst" ref={lijst}>
          {regels.map((r, i) => {
            const kop = r.groep !== vorige ? r.groep : null
            vorige = r.groep
            return (
              <div key={r.key}>
                {kop && <div className="zoek-groep">{kop}</div>}
                <button type="button" className={`zoek-regel ${i === keuze ? 'gekozen' : ''}`}
                  onMouseEnter={() => setKeuze(i)} onClick={() => doe(r)}>
                  <span className="zoek-icoon">{r.icoon}</span>
                  <span className="zoek-titel">{r.titel}</span>
                  {r.sub && <span className="zoek-sub">{r.sub}</span>}
                </button>
              </div>
            )
          })}
          {!regels.length && <p className="menu-leeg">Niets gevonden voor “{q}”.</p>}
        </div>
        <div className="zoek-voet">
          <span><kbd>↑</kbd><kbd>↓</kbd> kiezen</span>
          <span><kbd>Enter</kbd> openen</span>
          <span>Een taaknummer zoals <span className="mono">{lees().projecten[0]?.sleutel ?? 'WEB'}-12</span> gaat meteen goed</span>
        </div>
      </div>
    </div>,
    document.body,
  )
}
